import React, { FormEvent } from 'react';
import Highcharts from 'highcharts';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import { NumberFormatValues, SourceInfo } from 'react-number-format';
import {
  DisclosuresWrapper,
  DisclosureSymbolLink,
  DisclosureWithSymbol
} from '@ecs/tng-common/components/disclosures/Disclosures';
import { addCommas, scrollTo } from '@ecs/tng-common/scripts/helpers';
import { TabPane, Tabs } from 'react-bootstrap';
// import { Link } from 'gatsby';
import { CalculatorProps, drawHighChart, calcMonthlyInterestRate } from '../../helpers/calculators';
import { calcEducationalData } from '../../content/documents/disclosures';
import FormattedInput from '../FormattedInput';

type inputTypes = 'balance' | 'interestRate' | 'payment';
type calculationsType = {
  numPayments: number;
  monthlyPayment: number;
  totalInterestPaid: number;
  payoffDate: string;
  totalPayments: number;
  termInYears: number;
  principalPaidPercentage: number;
  interestPaidPercentage: number;
  tableData: {
    balance: number;
    interest: number;
    principal: number;
  }[];
};
type CreditCardCalcState = {
  cards: {
    balance: number;
    interestRate: number;
    payment: number;
    errors: {
      balance: string;
      interestRate: string;
      payment: string;
    };
    paymentWarningMsg?: string;
  }[];
  calculations: calculationsType;
};
const cardDefaultValues = {
  balance: 2000,
  interestRate: 15,
  payment: 45
};
const config = {
  title: 'Credit card payoff calculator',
  tableTabTitle1: 'Monthly payment',
  tableTabTitle2: 'Payment schedule',
  highChartTitle: 'Principal and interest',
  highChartLegendName1: 'Principal paid',
  highChartLegendName2: 'Interest paid',
  tableTh: ['#', 'Total amount paid', 'Total interest paid', 'Total principal paid', 'Balance']
};
const formConfig = (index: number) => {
  return {
    interestRate: {
      id: `interestRate${index}`,
      name: 'Interest rate (APR)',
      type: 'tel',
      allowNegative: false,
      required: true,
      suffix: ' %',
      isAllowed: (values: NumberFormatValues) => {
        const { floatValue } = values;
        return floatValue === undefined || floatValue <= 60;
      },
      allowLeadingZeros: false,
      errorMessages: {
        required: 'Interest rate value is required',
        min: 'Interest rate must be greater than 0.1%.'
      }
    },
    balance: {
      id: `balance${index}`,
      name: 'Credit card balance',
      type: 'tel',
      allowNegative: false,
      maxLength: 11,
      required: true,
      thousandSeparator: ',',
      prefix: '$ ',
      isAllowed: (values: NumberFormatValues) => {
        const { floatValue } = values;
        return floatValue === undefined || floatValue <= 1000000;
      },
      allowLeadingZeros: false,
      errorMessages: {
        required: 'Credit card balance is required'
      }
    },
    payment: {
      id: `payment${index}`,
      name: 'Monthly Payment',
      type: 'tel',
      allowNegative: false,
      maxLength: 11,
      required: true,
      thousandSeparator: ',',
      prefix: '$ ',
      isAllowed: (values: NumberFormatValues) => {
        const { floatValue } = values;
        return floatValue === undefined || floatValue <= 1000000;
      },
      allowLeadingZeros: false,
      errorMessages: {
        required: 'Value is required'
      }
    }
  };
};
export default class CreditCardCalc extends React.Component<CalculatorProps, CreditCardCalcState> {
  constructor(props: CalculatorProps) {
    super(props);
    this.state = {
      cards: [
        {
          ...cardDefaultValues,
          errors: { balance: '', interestRate: '', payment: '' }
        }
      ],
      calculations: {} as calculationsType
    };
  }

  componentDidMount() {
    if (this.props.isFull && typeof window !== 'undefined') {
      const queryParams = new URLSearchParams(window.location.search);

      // Rebuild the cards array
      const reconstructedCards = [];

      for (let i = 0; queryParams.has(`cards[${i}][balance]`); i++) {
        reconstructedCards.push({
          balance: Number(queryParams.get(`cards[${i}][balance]`)) || cardDefaultValues.balance,
          interestRate:
            Number(queryParams.get(`cards[${i}][interestRate]`)) || cardDefaultValues.interestRate,
          payment: Number(queryParams.get(`cards[${i}][payment]`)) || cardDefaultValues.payment,
          errors: { balance: '', interestRate: '', payment: '' }
        });
      }
      // If no parameters were found, use the default card
      if (reconstructedCards.length === 0) {
        reconstructedCards.push({
          ...cardDefaultValues,
          errors: { balance: '', interestRate: '', payment: '' }
        });
      }

      // Set the state with the reconstructed or default card
      this.setState({ cards: reconstructedCards }, () => {
        this.calculate(false);
      });
    }
    HighchartsAccessibility(Highcharts);
  }

  addCard = () => {
    this.setState(
      (prevState) => {
        const newCard = {
          ...cardDefaultValues,
          errors: {
            balance: '',
            interestRate: '',
            payment: ''
          }
        };

        // Create a new array with the new card inserted at the specified index
        const updatedCards = [...prevState.cards, newCard]; // Copy existing cards

        return { cards: updatedCards };
      },
      () => {
        // console.log(this.state.cards);
      }
    );
  };

  removeCard = (index: number) => () => {
    this.setState((prevState) => {
      const updatedCards = prevState.cards.filter((card, i) => i !== index);
      return { cards: updatedCards };
    });
  };

  handleOnBlur = (event: React.FocusEvent<HTMLInputElement>, index: number) => {
    const { name } = event.target as HTMLInputElement;
    const modifiedName = name.slice(0, -1);
    const { errors, balance, payment } = this.state.cards[index];

    let modifiedPayment = null;
    let paymentWarningMsg = '';
    // to update only of balance exist and not empty
    if (balance) {
      const minPayment = (balance * 0.0225).toFixed(0);
      const minPaymentValue = Math.max(Number(minPayment), 25); // Ensure minimum payment is at least 25

      if (
        modifiedName === 'balance' ||
        (modifiedName === 'payment' && (payment < minPaymentValue || payment > balance)) ||
        (modifiedName === 'payment' && !payment)
      ) {
        modifiedPayment = minPaymentValue;
        // Reset error if present
        errors.payment = '';
        if (!errors.payment && !errors.balance && !errors.interestRate) {
          if (payment < minPaymentValue || !payment) {
            paymentWarningMsg = `Monthly payment must be higher than the minimum payment of $${addCommas(modifiedPayment)}`;
          } else if (payment > balance) {
            paymentWarningMsg = `Monthly payment must be lower than the balance of $${addCommas(balance)}`;
          }
        }
      }
    }
    if (modifiedPayment) {
      this.setState((prevState) => ({
        cards: prevState.cards.map((card, i) =>
          i === index ? { ...card, payment: modifiedPayment, paymentWarningMsg, errors } : card
        )
      }));
    }
  };

  handleChange = (sourceInfo: SourceInfo, values: NumberFormatValues, index: number) => {
    const { event } = sourceInfo;
    if (!event) return;
    const { name, value } = sourceInfo.event?.target as HTMLInputElement;

    const modifiedName = name.slice(0, -1) as inputTypes;
    const { errors } = this.state.cards[index];
    const { floatValue } = values;
    const conf = formConfig(index);
    if (modifiedName === 'interestRate' && Number(floatValue) < 0.1) {
      errors[modifiedName] = conf.interestRate.errorMessages.min;
    } else {
      errors[modifiedName] =
        value === '' || Number.isNaN(value) || Number(value) <= 0
          ? conf[modifiedName].errorMessages.required
          : '';
    }
    this.setState((prevState) => ({
      cards: prevState.cards.map((card, i) =>
        i === index ? { ...card, [modifiedName]: floatValue, errors, paymentWarningMsg: '' } : card
      )
    }));
  };

  handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!this.checkIfFormIsInvalid()) {
      this.calculate(true);
    }
  };

  calculate(toScroll?: boolean) {
    const { cards } = this.state;

    const cardsCalcInfo = cards.map((card) => {
      const monthlyInterest = calcMonthlyInterestRate(card.interestRate);
      const minPayment = card.balance * monthlyInterest + card.balance * 0.01;
      const payment = Math.max(minPayment, card.payment);
      const loanTerm =
        -Math.log(1 - (monthlyInterest * card.balance) / payment) / Math.log(1 + monthlyInterest);

      return {
        balance: card.balance,
        payment,
        monthlyInterest,
        loanTerm
      };
    });
    const totalAmount = cardsCalcInfo.reduce((sum, card) => sum + card.balance, 0);
    const totalLoanTerm = Math.max(...cardsCalcInfo.map((card) => card.loanTerm));
    const numPayments = Math.ceil(totalLoanTerm);

    let totalInterest = 0;
    const tableData = Array.from({ length: numPayments }, () => ({
      balance: 0,
      interest: 0,
      principal: 0
    }));

    cardsCalcInfo.forEach((card) => {
      let { balance } = card;
      for (let i = 0; balance > 0; i++) {
        const interestForMonth = balance * card.monthlyInterest;
        const principalForMonth = Math.min(card.payment - interestForMonth, balance);
        balance -= principalForMonth;
        totalInterest += interestForMonth;
        tableData[i].balance += balance;
        tableData[i].interest += interestForMonth;
        tableData[i].principal += principalForMonth;
      }
    });

    const monthlyPayment = cardsCalcInfo.reduce((sum, card) => sum + card.payment, 0);
    const totalPayments = totalAmount + totalInterest;
    const totalInterestPaid = totalInterest;

    const payoffDateObj = new Date();
    payoffDateObj.setMonth(payoffDateObj.getMonth() + numPayments);
    const payoffMonth = payoffDateObj.toLocaleString('default', { month: 'long' });
    const payoffDate = payoffMonth + ' ' + payoffDateObj.getFullYear();
    const termInYears = Math.ceil(numPayments / 12);
    const principalPaidPercentage = parseFloat(
      (((totalPayments - totalInterestPaid) / totalPayments) * 100).toFixed(2)
    );
    const interestPaidPercentage = parseFloat(
      ((totalInterestPaid / totalPayments) * 100).toFixed(2)
    );
    this.setState(
      {
        calculations: {
          numPayments,
          monthlyPayment,
          totalInterestPaid,
          payoffDate,
          totalPayments,
          termInYears,
          tableData,
          principalPaidPercentage,
          interestPaidPercentage
        }
      },
      () => {
        if (typeof toScroll === 'undefined' || toScroll) {
          scrollTo('calcTotals', -24);
        }
      }
    );
  }

  addQueryParams(): string {
    const params = new URLSearchParams();
    this.state.cards.forEach((card, index) => {
      if (!card.errors.balance) {
        params.append(`cards[${index}][balance]`, card.balance?.toString());
      }
      if (!card.errors.interestRate) {
        params.append(`cards[${index}][interestRate]`, card.interestRate?.toString());
      }
      if (!card.errors.payment) {
        params.append(`cards[${index}][payment]`, card.payment?.toString());
      }
    });
    return params.toString();
  }

  checkIfFormIsInvalid() {
    const { cards } = this.state;

    const allErrorsEmpty = cards.every((item) =>
      Object.values(item.errors).every((error) => error === '')
    );
    return !allErrorsEmpty;
  }

  render() {
    const { isFull } = this.props;
    const { calculations, cards } = this.state;

    const renderForm = () => {
      return (
        <form
          className={`${calculations ? 'mb-4' : 'content-mb'} border rounded-3 p-3 p-lg-4 ${!isFull ? 'rounded-top-0' : ''}`}
          onSubmit={this.handleSubmit}>
          <div
            className={`col-sm-10 col-md-8 ${isFull ? 'col-lg-12 col-xl-12' : 'col-lg-9 col-xl-8'}`}>
            {cards.map((card, index) => {
              const conf = formConfig(index);
              return (
                <div
                  key={`${index}-form-card`}
                  className="row">
                  {index > 0 ? (
                    <div className="col-12 d-flex justify-content-between border-top pt-3">
                      <p className="h6">Card {index + 1}</p>
                      <button
                        type="button"
                        aria-label="Remove card"
                        className="btn btn-link font-icon-size-md"
                        onClick={this.removeCard(index)}>
                        <span className="ecs-font-icon-act-close" />
                      </button>
                    </div>
                  ) : null}
                  <div className="col-12">
                    <div className="mb-3 form-floating ">
                      <FormattedInput
                        config={conf.balance}
                        value={card.balance?.toString()}
                        errors={card.errors?.balance}
                        onValueChange={(values: NumberFormatValues, sourceInfo: SourceInfo) =>
                          this.handleChange(sourceInfo, values, index)
                        }
                        onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                          this.handleOnBlur(event, index)
                        }
                        placeholder={card.balance?.toString()}
                      />
                      {card.errors?.balance && (
                        <div className="invalid-feedback">{card.errors?.balance}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="mb-3 form-floating">
                      <FormattedInput
                        config={conf.interestRate}
                        value={card.interestRate?.toString()}
                        errors={card.errors?.interestRate}
                        onValueChange={(values: NumberFormatValues, sourceInfo: SourceInfo) =>
                          this.handleChange(sourceInfo, values, index)
                        }
                        placeholder={card.interestRate?.toString()}
                      />
                      {card.errors?.interestRate && (
                        <div className="invalid-feedback">{card.errors?.interestRate}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="mb-3 form-floating">
                      <FormattedInput
                        config={conf.payment}
                        value={card.payment?.toString()}
                        errors={card.errors?.payment} // Corrected from errors.term to errors.payment
                        onValueChange={(values: NumberFormatValues, sourceInfo: SourceInfo) =>
                          this.handleChange(sourceInfo, values, index)
                        }
                        onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                          this.handleOnBlur(event, index)
                        }
                        placeholder={card.payment?.toString()}
                      />
                      {card.errors?.payment && (
                        <div className="invalid-feedback">{card.errors?.payment}</div>
                      )}
                    </div>
                  </div>
                  {card.paymentWarningMsg ? (
                    <div className="col-12">
                      <p className="body-text-xs text-gray">{card.paymentWarningMsg}</p>
                    </div>
                  ) : null}
                </div>
              );
            })}
            {cards.length < 6 ? (
              <button
                type="button"
                className="btn btn-link"
                onClick={this.addCard}>
                <span className="ecs-font-icon-act-plus" /> Add card
              </button>
            ) : null}
            <button
              disabled={this.checkIfFormIsInvalid()}
              type="submit"
              className="btn btn-secondary mt-3 d-block">
              Calculate
            </button>
          </div>
        </form>
      );
    };
    const renderTable = () => {
      const paymentsArray = Array.from(
        { length: calculations.numPayments },
        (_, index) => index + 1
      );
      return (
        <div
          className={`${calculations.numPayments > 11 ? 'table-vertical-scroll' : ''} table-horizontal-scroll-sm`}>
          <table className="table table-striped">
            <thead>
              <tr>
                {config.tableTh.map((th) => (
                  <th key={th}>{th}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paymentsArray.map((paymentNumber) => {
                const tableData = calculations.tableData[paymentNumber - 1];
                return (
                  <tr key={paymentNumber}>
                    <td>{paymentNumber}</td>
                    <td>${addCommas(calculations.monthlyPayment.toFixed(0))}</td>
                    <td>${addCommas(tableData.interest.toFixed(0))}</td>
                    <td>${addCommas(tableData.principal.toFixed(0))}</td>
                    <td>${addCommas(tableData.balance.toFixed(0))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    };
    const renderTotals = () => {
      return (
        <div className={`row gx-md-6 ${isFull ? 'pt-4 gy-4' : 'pt-3 pt-lg-4 gy-3 gy-lg-4'}`}>
          <div className="col-md-6 col-lg-12 col-xl-6">
            <div className="bg-light rounded-3 p-4 d-flex flex-wrap text-center mb-5">
              <span className="text-gray w-100">You will pay off your debt by</span>
              <span className="h2 mb-0 w-100">{calculations.payoffDate}</span>
              {calculations.numPayments > 7 ? (
                <span className="text-gray w-100">(or {calculations.termInYears} years)</span>
              ) : null}
            </div>
            <div className="border-bottom d-flex justify-content-between pb-2 pb-lg-3">
              <span className="text-gray">Number of payments:</span>
              <span className="fw-bold">{calculations.numPayments}</span>
            </div>
            <div className="border-bottom d-flex justify-content-between py-2 py-lg-3">
              <span className="text-gray">
                Estimated monthly payment
                <DisclosureSymbolLink disclosure={calcEducationalData} />
              </span>
              <span className="fw-bold">${addCommas(calculations.monthlyPayment.toFixed(2))}</span>
            </div>
            <div className="border-bottom d-flex justify-content-between py-2 py-lg-3">
              <span className="text-gray">Total interest paid:</span>
              <span className="fw-bold">
                ${addCommas(calculations.totalInterestPaid.toFixed(2))}
              </span>
            </div>
            <div className="d-flex justify-content-between pt-2 pt-lg-3">
              <span className="text-gray">Total payments:</span>
              <span className="fw-bold">${addCommas(calculations.totalPayments.toFixed(2))}</span>
            </div>
          </div>
          {drawHighChart({
            title: config.highChartTitle,
            option1: {
              name: config.highChartLegendName1,
              value: calculations.principalPaidPercentage
            },
            option2: {
              name: config.highChartLegendName2,
              value: calculations.interestPaidPercentage
            }
          })}
        </div>
      );
    };
    const renderCalculationsBox = () => {
      return Object.keys(calculations).length > 0 ? (
        <>
          <div
            id="calcTotals"
            className="result border rounded-3 overflow-hidden body-text-sm">
            <div className={`px-3 pb-3 pb-lg-4 px-lg-4 ${isFull ? 'pt-3 pt-lg-4' : ''}`}>
              {isFull ? (
                <Tabs
                  id="calc-tab"
                  defaultActiveKey="calcTab1"
                  className="nav-underline">
                  <TabPane
                    eventKey="calcTab1"
                    title={config.tableTabTitle1}>
                    {renderTotals()}
                  </TabPane>
                  <TabPane
                    eventKey="calcTab2"
                    title={config.tableTabTitle2}>
                    {renderTable()}
                  </TabPane>
                </Tabs>
              ) : (
                renderTotals()
              )}
            </div>
            <DisclosuresWrapper className="bg-light p-3 p-lg-4 text-gray">
              <DisclosureWithSymbol disclosure={calcEducationalData} />
            </DisclosuresWrapper>
          </div>
          {/* // REPLACE WITH GATSBY LINK */}
          {!isFull && (
            <p className="body-text-sm mt-3 d-flex align-items-center mb-6 mb-lg-7">
              <span className="ecs-font-icon-con-calculator font-icon-size-md" /> Try the full&nbsp;
              <a
                href={`/blogs/ask-experian/credit-card-payoff-calculator/?${this.addQueryParams()}`}>
                credit card payoff calculator
              </a>
              &nbsp;with more features.
            </p>
          )}
        </>
      ) : null;
    };
    return isFull ? (
      <div className="d-lg-flex row content-mt">
        <div className="col-lg-5 col-xl-4">{renderForm()}</div>
        <div className="col-lg-7 col-xl-8">{renderCalculationsBox()}</div>
      </div>
    ) : (
      <>
        <h4 className="calc-title mb-0 rounded-3 rounded-bottom-0 p-3 py-lg-4 px-lg-5 bg-midnight-blue text-white content-mt">
          {config.title}
        </h4>
        {renderForm()}
        {renderCalculationsBox()}
      </>
    );
  }
}
